import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import RadioInput from '~/ui/RadioInput/RadioInput';
import { Action } from '~/ui/Actions';
import AddIcon from '~/assets/svg/newSvg/actions/add.svg?react';
import PromotionConditionTable from '../PromotionConditionTable/PromotionConditionTable';
import { ICondition } from '~/components/PromotionsBoard/StockBoard/StockBoard';
import cn from 'classnames';

import styles from './ConditionGroupCard.module.scss';

export interface IConditionGroup {
  id: string;
  operator: 'and' | 'or';
  inter_operator?: 'and' | 'or';
  bindings: ICondition[];
}

interface IConditionGroupCardProps {
  group: IConditionGroup;
  groupIndex: number;
  disabled?: boolean;
  isEdit?: boolean;
  canDelete: boolean;
  hasError?: boolean;
  onOperatorChange: (groupId: string, operator: 'and' | 'or') => void;
  onAddCondition: (groupId: string, condition: ICondition) => void;
  onDeleteCondition: (groupId: string, conditionId: string, item: { rule_id: number }) => void;
  onEditCondition: (groupId: string, condition: ICondition) => void;
  onDeleteGroup: (groupId: string) => void;
}

const ConditionGroupCard = ({
  group,
  groupIndex,
  disabled = false,
  isEdit = false,
  canDelete,
  hasError = false,
  onOperatorChange,
  onAddCondition,
  onDeleteCondition,
  onEditCondition,
  onDeleteGroup,
}: IConditionGroupCardProps) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ field: '', direction: '' });
  const [isAddingSlot, setIsAddingSlot] = useState(false);

  const toggleAddingSlot = () => {
    setIsAddingSlot(prevState => !prevState);
  };

  return (
    <div className={cn(styles.groupCard, { [styles.groupCardError]: hasError })}>
      <div className={styles.groupHeader}>
        <div className={styles.groupTitleRow}>
          <span className={styles.groupTitle}>
            {t('condition_group')} {groupIndex + 1}
          </span>
          {canDelete && (
            <button
              type="button"
              className={styles.deleteGroupBtn}
              onClick={() => onDeleteGroup(group.id)}
              disabled={disabled}
            >
              {t('delete_group')}
            </button>
          )}
        </div>
        <div className={styles.operatorWrapper}>
          <span className={styles.operatorLabel}>{t('group_operator')}:</span>
          <div className={styles.operatorRadios}>
            <RadioInput
              label={t('condition_and')}
              name={`group_operator_${group.id}`}
              value="and"
              checkValue={group.operator}
              onChange={() => onOperatorChange(group.id, 'and')}
              disabled={disabled}
            />
            <RadioInput
              label={t('condition_or')}
              name={`group_operator_${group.id}`}
              value="or"
              checkValue={group.operator}
              onChange={() => onOperatorChange(group.id, 'or')}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <div className={styles.conditionBoardHeader}>
        <Action
          variant="success"
          onClick={toggleAddingSlot}
          className={styles.actionBtn}
          disabled={disabled}
        >
          <AddIcon /> {t('add_condition')}
        </Action>
      </div>

      <PromotionConditionTable
        disabled={isEdit}
        sort={sort}
        setSort={setSort}
        conditions={group.bindings}
        isAdding={isAddingSlot}
        onDeleteAddingSlotClick={toggleAddingSlot}
        handleAddPromotion={(condition: ICondition) => onAddCondition(group.id, condition)}
        handleDeleteCondition={(id: string, item: { rule_id: number }) =>
          onDeleteCondition(group.id, id, item)
        }
        handleEditCondition={(condition: ICondition) => onEditCondition(group.id, condition)}
      />
    </div>
  );
};

export default ConditionGroupCard;
